import { Check, Download, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { tourAnchor } from '~/helpers/tour/anchors'

export type PathDocument = { id: string, title: string, requiredBeforeStart: boolean, acknowledgedAt?: unknown }

type IProps = {
  documents: readonly PathDocument[]
  formatDate: (value: unknown) => string
  onDownload: (document: PathDocument) => void
  onAcknowledge: (document: PathDocument) => void
  /** Le document dont l'accusé part, pour ne pas le renvoyer deux fois. */
  acknowledgingId?: string | null
}

/**
 * Les documents du parcours (#326) : à télécharger, et à déclarer lus. Ceux à
 * accepter avant de commencer le disent, puisqu'ils ferment les modules.
 */
function PathDocumentsCard({ documents, formatDate, onDownload, onAcknowledge, acknowledgingId }: IProps) {
  const { t } = useTranslation()
  if (documents.length === 0) return null

  return (
    <section className="mb-6 rounded-2xl border border-border bg-card p-5" aria-labelledby="path-documents-title" {...tourAnchor('path.documents')}>
      <h2 id="path-documents-title" className="mb-3 font-medium">{t('pathDocuments.title')}</h2>
      <ul className="flex flex-col divide-y divide-border">
        {documents.map(d => (
          <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
            <div className="flex min-w-0 items-center gap-2">
              <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="break-words text-sm font-medium">{d.title}</span>
              {d.requiredBeforeStart && !d.acknowledgedAt && <Badge variant="warningSubtle">{t('pathDocuments.required')}</Badge>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => onDownload(d)} aria-label={t('common.actionFor', { action: t('pathDocuments.download'), name: d.title })}>
                <Download className="mr-2 size-4" aria-hidden />
                {t('pathDocuments.download')}
              </Button>
              {d.acknowledgedAt
                ? (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Check className="size-4 text-success-text" aria-hidden />
                      {t('pathDocuments.readOn', { date: formatDate(d.acknowledgedAt) })}
                    </span>
                  )
                : (
                    <Button size="sm" onClick={() => onAcknowledge(d)} disabled={acknowledgingId === d.id} aria-label={t('common.actionFor', { action: t('pathDocuments.acknowledge'), name: d.title })}>
                      {t('pathDocuments.acknowledge')}
                    </Button>
                  )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default PathDocumentsCard
